import React from 'react';
import { X, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DateInput } from './DateInput';

interface Opportunity {
  id: string;
  origin: string;
  destination: string;
  departure_dates: string[];
  return_dates: string[];
  value: number;
  miles: number;
  fees_included: boolean;
  loyalty_program: string;
}

interface EditOpportunityModalProps {
  opportunity: Opportunity;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditOpportunityModal({ opportunity, onClose, onSuccess }: EditOpportunityModalProps) {
  const [form, setForm] = React.useState({
    origin: opportunity.origin,
    destination: opportunity.destination,
    departureDates: opportunity.departure_dates.map(date => new Date(date)),
    returnDates: opportunity.return_dates.map(date => new Date(date)),
    value: opportunity.value,
    miles: opportunity.miles,
    feesIncluded: opportunity.fees_included,
    loyaltyProgram: opportunity.loyalty_program,
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase
        .from('opportunities')
        .update({
          origin: form.origin,
          destination: form.destination,
          departure_dates: form.departureDates,
          return_dates: form.returnDates,
          value: form.value,
          miles: form.miles,
          fees_included: form.feesIncluded,
          loyalty_program: form.loyaltyProgram,
        })
        .eq('id', opportunity.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError('Erro ao atualizar oportunidade');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Editar Oportunidade
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Origem
                </label>
                <input
                  required
                  type="text"
                  value={form.origin}
                  onChange={(e) => setForm({ ...form, origin: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destino
                </label>
                <input
                  required
                  type="text"
                  value={form.destination}
                  onChange={(e) => setForm({ ...form, destination: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datas de Ida
                </label>
                <DateInput
                  dates={form.departureDates}
                  onChange={(dates) => setForm({ ...form, departureDates: dates })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datas de Volta
                </label>
                <DateInput
                  dates={form.returnDates}
                  onChange={(dates) => setForm({ ...form, returnDates: dates })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor
                </label>
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milhas
                </label>
                <input
                  required
                  type="number"
                  min="0"
                  value={form.miles}
                  onChange={(e) => setForm({ ...form, miles: Number(e.target.value) })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Programa de Fidelidade
                </label>
                <input
                  required
                  type="text"
                  value={form.loyaltyProgram}
                  onChange={(e) => setForm({ ...form, loyaltyProgram: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="feesIncluded"
                  checked={form.feesIncluded}
                  onChange={(e) => setForm({ ...form, feesIncluded: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="feesIncluded" className="ml-2 block text-sm text-gray-700">
                  Taxas Inclusas
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save size={20} />
                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}